import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Shield, Sun, Moon, Eye, EyeOff, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { getSSOConfig } from '../services/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoConfig, setSsoConfig] = useState({ enabled: false, authorize_url: null });
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // Load SSO config on mount
    const loadSSOConfig = async () => {
      try {
        const response = await getSSOConfig();
        setSsoConfig(response.data);
      } catch (error) {
        console.log('SSO not available');
      }
    };
    loadSSOConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Добро пожаловать, ${user.full_name}!`);
      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'security') {
        navigate('/security');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка авторизации');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    setSsoLoading(true);
    try {
      // Get fresh SSO config with new state
      const response = await getSSOConfig();
      if (response.data.enabled && response.data.authorize_url) {
        // Redirect to SSO authorization page
        window.location.href = response.data.authorize_url;
      } else {
        toast.error('SSO не настроен');
      }
    } catch (error) {
      toast.error('Ошибка подключения к SSO');
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Login form */}
      <div className="flex flex-col justify-center px-8 py-12 lg:px-16 xl:px-24 relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="absolute top-6 right-6"
          data-testid="theme-toggle"
        >
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>

        <div className="w-full max-w-md mx-auto space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-3 animate-fade-in">
            <div className="p-3 rounded-md bg-primary">
              <Shield className="h-8 w-8 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tighter">SENTINEL</h1>
              <p className="text-sm text-muted-foreground tracking-wide">ACCESS CONTROL</p>
            </div>
          </div>

          {/* Login Card */}
          <Card className="border-0 shadow-xl animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold">Вход в систему</CardTitle>
              <CardDescription>
                Введите данные для авторизации
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@company.ru"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Пароль</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password"
                      className="h-11 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 font-semibold transition-transform active:scale-95"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Вход...' : 'Войти'}
                </Button>

                {/* SSO Login */}
                {ssoConfig.enabled && (
                  <>
                    <div className="relative my-4">
                      <Separator />
                      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                        или
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11 font-semibold transition-transform active:scale-95"
                      onClick={handleSSOLogin}
                      disabled={ssoLoading}
                      data-testid="sso-login"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      {ssoLoading ? 'Переход...' : 'Войти через корпоративный SSO'}
                    </Button>
                  </>
                )}
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Система контроля доступа на территорию предприятия
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.pexels.com/photos/35293594/pexels-photo-35293594.jpeg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/70 to-transparent" />
        <div className="absolute inset-0 backdrop-blur-[2px]" />
        
        {/* Overlay content */}
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="space-y-4 max-w-lg">
            <h2 className="text-4xl font-black tracking-tight leading-tight">
              Безопасность начинается с контроля
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              Автоматизированная система управления пропусками для эффективного контроля доступа на территорию предприятия.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
